import React from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import { XMarkIcon } from "@heroicons/react/24/outline"

type MobileBottomSheetVariant = "light" | "dark"

type MobileBottomSheetProps = {
  open: boolean
  onClose: () => void
  ariaLabel: string
  variant: MobileBottomSheetVariant
  hideAbove?: "sm" | "lg"
  children: React.ReactNode
}

const SWIPE_DISMISS_THRESHOLD = 100

const VARIANT_STYLES: Record<
  MobileBottomSheetVariant,
  { panel: string; closeButton: string; handle: string }
> = {
  light: {
    panel: "bg-white",
    closeButton: "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800",
    handle: "bg-zinc-300",
  },
  dark: {
    panel: "bg-zinc-800",
    closeButton: "text-zinc-400 hover:bg-zinc-700/80 hover:text-zinc-200",
    handle: "bg-zinc-600",
  },
}

const HIDE_ABOVE_CLASS: Record<"sm" | "lg", string> = {
  sm: "sm:hidden",
  lg: "lg:hidden",
}

function MobileBottomSheet({
  open,
  onClose,
  ariaLabel,
  variant,
  hideAbove = "sm",
  children,
}: MobileBottomSheetProps) {
  if (typeof document === "undefined") return null

  const styles = VARIANT_STYLES[variant]
  const responsiveHide = HIDE_ABOVE_CLASS[hideAbove]

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div
          key="mobile-bottom-sheet"
          className={`fixed inset-0 z-50 ${responsiveHide}`}
          role="dialog"
          aria-label={ariaLabel}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              if (info.offset.y > SWIPE_DISMISS_THRESHOLD) onClose()
            }}
            className={`fixed inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl px-5 pb-8 pt-3 shadow-2xl ${styles.panel}`}
          >
            <div className="mb-3 flex justify-center">
              <div className={`h-1 w-10 rounded-full ${styles.handle}`} />
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`absolute right-3 top-3 rounded-md p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-kkm-green/40 ${styles.closeButton}`}
              aria-label="Sulje"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="mt-1">{children}</div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}

export default MobileBottomSheet
